// ─── The four figures at the top of the CAPA page ────────────────────────────
//
// Open, Started, Closed, Overdue — each a count, a "this week" figure, and a
// breakdown by the kind of run the CAPA was raised inside.
//
// Kept out of the component because every one of these is a definition someone
// will argue about, and a definition that lives in JSX has no tests. What each
// box counts is stated once, here, and pinned by capaStats.test.ts.
//
// ## What a CAPA's status means
//
// A CAPA is a capa-category FORM raised inside a running procedure, so it
// carries the form lifecycle: draft → active → completed, with cancelled as the
// way out. Mapped onto the four boxes:
//
//   Open      draft or active — raised and not yet closed out.
//   Started   active only — someone has begun filling it in.
//   Closed    completed.
//   Overdue   open, with its run's target date already past.
//
// `cancelled` is in NONE of them, deliberately. A withdrawn CAPA was never
// closed out, and counting it as closed would flatter the closure figure;
// counting it as open would keep dead work on the board forever.
//
// Started is a SUBSET of Open, not a slice beside it. The four boxes are four
// questions, not a partition, and 12 open of which 5 started is the useful
// reading — a reader who wants "raised but untouched" subtracts.

import type { CapaRecord, ProcedureType } from '@/types/cgt'
import { PROCEDURE_TYPE_LABELS, PROCEDURE_TYPES } from '@/types/cgt'

export type CapaStatKey = 'open' | 'started' | 'closed' | 'overdue'

/** One CAPA, flattened with the bits of its run the figures need. */
export interface CapaStatInput {
  capa: Pick<CapaRecord, 'status' | 'createdAt'> & { completedAt?: string | null }
  /** The run's target completion. Null when nobody set one. */
  dueDate: string | null
  /** The kind of run it was raised inside. Null on a run with no template type. */
  type: ProcedureType | null
}

export interface CapaTypeCount {
  type: ProcedureType
  label: string
  count: number
}

export interface CapaStat {
  key: CapaStatKey
  label: string
  count: number
  /**
   * How many of these happened in the last 7 days, or null where the question
   * has no answer — see `thisWeek` below.
   */
  thisWeek: number | null
  /** What the "this week" figure counts, for the caption. */
  thisWeekVerb: string | null
  byType: CapaTypeCount[]
}

const WEEK_MS = 7 * 24 * 60 * 60 * 1000

// A CAPA waiting for its reviews (z8nrz7dt41) is still open work.
const OPEN_STATUSES: CapaRecord['status'][] = ['draft', 'active', 'pending_review']

export function isOpen(capa: CapaStatInput['capa']): boolean {
  return OPEN_STATUSES.includes(capa.status)
}

export function isStarted(capa: CapaStatInput['capa']): boolean {
  return capa.status === 'active'
}

export function isClosed(capa: CapaStatInput['capa']): boolean {
  return capa.status === 'completed'
}

/**
 * Open, and its run's target date has passed.
 *
 * A CAPA with no due date is NOT overdue — there is no target to miss, and
 * counting it would make the figure grow with every run nobody scheduled.
 * A closed one is never overdue either, however late it was: the box asks
 * what still needs attention.
 */
export function isOverdue(row: CapaStatInput, now: Date): boolean {
  if (!isOpen(row.capa)) return false
  if (!row.dueDate) return false
  const due = new Date(row.dueDate).getTime()
  return Number.isFinite(due) && due < now.getTime()
}

/** Whether an ISO instant falls inside the 7 days ending now. */
function withinWeek(iso: string | null | undefined, now: Date): boolean {
  if (!iso) return false
  const t = new Date(iso).getTime()
  if (!Number.isFinite(t)) return false
  return t <= now.getTime() && t > now.getTime() - WEEK_MS
}

/**
 * Counts per run kind, in the order the domain lists them, omitting the kinds
 * with nothing in them.
 *
 * A CAPA on a run whose template has no type is counted in the total but in no
 * kind — better a breakdown that does not add up to the headline than a made-up
 * bucket, and the headline is the number a reader acts on.
 */
function breakdown(rows: CapaStatInput[]): CapaTypeCount[] {
  return PROCEDURE_TYPES.map((type) => ({
    type,
    label: PROCEDURE_TYPE_LABELS[type],
    count: rows.filter((r) => r.type === type).length,
  })).filter((entry) => entry.count > 0)
}

function stat(
  key: CapaStatKey,
  label: string,
  rows: CapaStatInput[],
  thisWeek: number | null,
  thisWeekVerb: string | null,
): CapaStat {
  return { key, label, count: rows.length, thisWeek, thisWeekVerb, byType: breakdown(rows) }
}

/**
 * The four boxes, in the order they are shown.
 *
 * `now` is passed in rather than read here so the figures are testable and so
 * one render cannot disagree with itself halfway down the row.
 */
export function capaStats(rows: CapaStatInput[], now: Date): CapaStat[] {
  const open = rows.filter((r) => isOpen(r.capa))
  const started = rows.filter((r) => isStarted(r.capa))
  const closed = rows.filter((r) => isClosed(r.capa))
  const overdue = rows.filter((r) => isOverdue(r, now))

  return [
    stat('open', 'CAPA Open', open, countWithin(open, (r) => r.capa.createdAt, now), 'raised'),
    stat(
      'started',
      'CAPA Started',
      started,
      countWithin(started, (r) => r.capa.createdAt, now),
      'raised',
    ),
    stat(
      'closed',
      'CAPA Closed',
      closed,
      countWithin(closed, (r) => r.capa.completedAt ?? null, now),
      'closed',
    ),
    // No weekly figure: "overdue this week" is not a thing that happens on a
    // date. A CAPA slips past its target and stays there.
    stat('overdue', 'CAPA Overdue', overdue, null, null),
  ]
}

function countWithin(
  rows: CapaStatInput[],
  pick: (row: CapaStatInput) => string | null | undefined,
  now: Date,
): number {
  return rows.filter((row) => withinWeek(pick(row), now)).length
}

/** "Transplant (2) · MES (3)", or a dash when nothing is counted by kind. */
export function breakdownLabel(byType: CapaTypeCount[]): string {
  if (byType.length === 0) return '—'
  return byType.map((entry) => `${entry.label} (${entry.count})`).join(' · ')
}
